import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { createWorkspace } from "./api/endpoints";
import { canWrite, useAuth } from "./auth/AuthContext";
import { useToast } from "./components/Toast";
import { useWorkspaceEnv } from "./state/WorkspaceEnvContext";

const NAV = [
  { to: "/flags", label: "Flags" },
  { to: "/segments", label: "Segments" },
  { to: "/keys", label: "SDK keys" },
  { to: "/audit", label: "Audit log" },
  { to: "/evaluate", label: "Evaluate" },
  { to: "/impressions", label: "Impressions" },
];

export function Layout({ children }: { children: ReactNode }) {
  const { identity, signOut, switchVendor } = useAuth();
  const {
    loading,
    workspaces,
    environments,
    workspace,
    environment,
    selectWorkspace,
    selectEnvironment,
    reload,
  } = useWorkspaceEnv();
  const vendors = identity?.via === "identity" ? (identity.vendors ?? []) : [];
  const needsSetup = !loading && workspaces.length === 0 && identity?.via === "identity";

  return (
    <div className="app-shell">
      <header className="app-header">
        <span className="app-title">Spoonity Flags</span>

        {vendors.length > 1 && (
          <select
            className="select"
            value={identity?.vendorId ?? ""}
            onChange={(e) => void switchVendor(e.target.value)}
            aria-label="Vendor"
          >
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        )}

        <select
          className="select"
          value={workspace?.id ?? ""}
          onChange={(e) => selectWorkspace(e.target.value)}
          aria-label="Workspace"
        >
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={environment?.id ?? ""}
          onChange={(e) => selectEnvironment(e.target.value)}
          aria-label="Environment"
        >
          {environments.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>

        <nav className="app-nav">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => (isActive ? "active" : "")}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="identity">
          {identity && (
            <>
              <span>{identity.email}</span>
              <span className={`role-badge ${identity.role}`}>{identity.role}</span>
              <button className="btn small" onClick={signOut}>
                Sign out
              </button>
            </>
          )}
        </div>
      </header>
      <main className="app-main">{needsSetup ? <WorkspaceSetup onCreated={reload} /> : children}</main>
    </div>
  );
}

/**
 * An Identity session whose vendor has no workspace yet (CONTRACT §2.4): an
 * editor or admin may create it for their own vendor, nobody else's.
 */
function WorkspaceSetup({ onCreated }: { onCreated: () => Promise<void> }) {
  const { identity } = useAuth();
  const { pushError } = useToast();
  const [creating, setCreating] = useState(false);
  const vendorName = identity?.vendorName ?? identity?.vendorId ?? "this vendor";

  return (
    <div className="card workspace-setup">
      <h2>Flags is not set up for {vendorName} yet</h2>
      {canWrite(identity?.role) && identity?.vendorId ? (
        <>
          <p className="muted">Create the workspace to start adding flags and environments.</p>
          <button
            className="btn primary"
            type="button"
            disabled={creating}
            onClick={async () => {
              setCreating(true);
              try {
                await createWorkspace({ vendorId: identity.vendorId!, name: vendorName.slice(0, 120) });
                await onCreated();
              } catch (err) {
                pushError(err, "Could not create the workspace");
              } finally {
                setCreating(false);
              }
            }}
          >
            Set up Flags for {vendorName}
          </button>
        </>
      ) : (
        <p className="muted">Ask an admin for {vendorName} to set it up.</p>
      )}
    </div>
  );
}
